import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle, ShoppingCartSimple, WhatsappLogo, ArrowRight, DeviceMobileSpeaker } from "@phosphor-icons/react";
import { fmtMoney } from "./HomeProductCard";

const CONDITION = { nuevo: "Nuevo", seminuevo: "Seminuevo", reacondicionado: "Reacondicionado", usado: "Usado" };
const SPEC_LABELS = { cpu: "Procesador", ram: "Memoria", storage: "Almacenamiento", screen: "Pantalla", gpu: "Gráficos", os: "Sistema", battery: "Batería", resolution: "Resolución", refresh_rate: "Frecuencia", size: "Tamaño", connectivity: "Conectividad", capacity: "Capacidad", wattage: "Potencia", connector: "Conector", camera: "Cámara" };
const ORDER = ["cpu", "ram", "storage", "screen", "gpu", "os", "battery", "resolution", "refresh_rate", "size", "capacity", "wattage", "connector", "camera", "connectivity"];

// Vista rápida (mockup: modal "Ver equipo"): lo importante para decidir, luego el detalle.
export default function QuickViewModal({ product: p, onClose, onAdd, companyName, whatsapp }) {
  const [idx, setIdx] = useState(0);
  if (!p) return null;
  const photos = (p.photos || []).filter(Boolean);
  const currency = p.sale_currency || "MXN";
  const store = Number(p.store_price || 0);
  const cmp = Number(p.compare_at_price || 0);
  const specs = p.specs || {};
  const tiles = [...ORDER.filter((k) => specs[k]), ...Object.keys(specs).filter((k) => !ORDER.includes(k) && specs[k])].slice(0, 6);
  const waHref = whatsapp ? `https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hola, me interesa: ${p.name} (${fmtMoney(store, currency)}). ¿Sigue disponible?`)}` : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[92vh] overflow-y-auto p-0" data-testid="quick-view">
        <div className="grid md:grid-cols-2">
          <div className="bg-zinc-50 p-5 flex flex-col">
            <div className="aspect-square rounded-xl bg-white border border-zinc-100 flex items-center justify-center overflow-hidden">
              {photos[idx] ? <img src={photos[idx]} alt={p.name} className="w-full h-full object-contain p-3" /> : <DeviceMobileSpeaker size={64} className="text-zinc-300" />}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto">
                {photos.map((ph, i) => (
                  <button type="button" key={i} onClick={() => setIdx(i)} className={`w-14 h-14 rounded-md border overflow-hidden shrink-0 ${i === idx ? "border-[#002FA7]" : "border-zinc-200"}`}><img src={ph} alt="" className="w-full h-full object-cover" /></button>
                ))}
              </div>
            )}
          </div>
          <div className="p-6 flex flex-col">
            {p.condition && CONDITION[p.condition] && (
              <div className="text-[#00873D] font-bold text-xs inline-flex items-center gap-1" data-testid="qv-condition"><CheckCircle size={13} weight="fill" /> {CONDITION[p.condition]}</div>
            )}
            {p.brand && <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mt-1">{p.brand}</div>}
            <DialogTitle className="font-heading text-xl sm:text-2xl font-extrabold leading-tight mt-0.5" data-testid="qv-name">{p.name}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-500 mt-1">{p.short_tagline || "Primero lo importante para decidir, después los detalles técnicos."}</DialogDescription>
            {tiles.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mt-4" data-testid="qv-specs">
                {tiles.map((k) => (
                  <div key={k} className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{SPEC_LABELS[k] || k.replace(/_/g, " ")}</div>
                    <div className="text-sm font-semibold text-zinc-900 truncate" title={specs[k]}>{specs[k]}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4">
              {cmp > store && <div className="text-xs text-zinc-400 line-through">{fmtMoney(cmp, currency)}</div>}
              <div className={`font-heading text-3xl font-extrabold ${cmp > store ? "text-[#E0000F]" : "text-zinc-900"}`} data-testid="qv-price">{fmtMoney(store, currency)}</div>
            </div>
            <ul className="text-xs text-zinc-600 mt-3 space-y-0.5">
              <li>✓ Revisado por {companyName}</li><li>✓ Garantía</li><li>✓ Atención personalizada</li>
            </ul>
            <div className="mt-auto pt-5 space-y-2">
              <button type="button" onClick={() => onAdd(p)} disabled={!p.max_qty} className="w-full inline-flex items-center justify-center gap-2 bg-[#002FA7] hover:bg-[#0043c2] disabled:bg-zinc-300 text-white font-bold py-3 rounded-full text-sm" data-testid="qv-add">
                <ShoppingCartSimple size={16} weight="bold" /> {p.max_qty ? "Agregar al carrito" : "Agotado"}
              </button>
              <div className={`grid gap-2 ${waHref ? "grid-cols-2" : "grid-cols-1"}`}>
                {waHref && (
                  <a href={waHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 border border-[#25D366] text-[#128C7E] font-semibold py-2.5 rounded-full text-xs hover:bg-[#25D366]/10" data-testid="qv-whatsapp"><WhatsappLogo size={15} weight="fill" /> Preguntar por WhatsApp</a>
                )}
                <Link to={`/catalogo/p/${p.id}`} className="inline-flex items-center justify-center gap-1 border border-zinc-200 text-zinc-700 font-semibold py-2.5 rounded-full text-xs hover:border-[#002FA7] hover:text-[#002FA7]" data-testid="qv-full">Ficha completa <ArrowRight size={13} weight="bold" /></Link>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
